// Simple toast notification system for save operations feedback

export type ToastOptions = {
  type?: 'error' | 'info' | 'warning'
  duration?: number
  position?:
    | 'top-right'
    | 'top-center'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'bottom-left'
}

export type Toast = {
  id: string
  message: string
  type: 'error' | 'info' | 'warning'
  duration: number
  timestamp: number
}

type ToastListener = (toasts: Toast[]) => void

class ToastManager {
  private toasts: Toast[] = []
  private listeners: Set<ToastListener> = new Set()
  private idCounter = 0

  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener)
    // Immediately call with current toasts
    listener(this.toasts)

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify() {
    this.listeners.forEach((listener) => listener(this.toasts))
  }

  show(message: string, options: ToastOptions = {}) {
    const { type = 'info', duration = 3000 } = options

    const toast: Toast = {
      id: `toast-${++this.idCounter}`,
      message,
      type,
      duration,
      timestamp: Date.now(),
    }

    this.toasts.push(toast)
    this.notify()

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(toast.id)
      }, duration)
    }

    return toast.id
  }

  remove(id: string) {
    this.toasts = this.toasts.filter((toast) => toast.id !== id)
    this.notify()
  }

  clear() {
    this.toasts = []
    this.notify()
  }

  error(message: string, options?: Omit<ToastOptions, 'type'>) {
    return this.show(message, { ...options, type: 'error', duration: 5000 }) // Longer for errors
  }

  info(message: string, options?: Omit<ToastOptions, 'type'>) {
    return this.show(message, { ...options, type: 'info' })
  }

  warning(message: string, options?: Omit<ToastOptions, 'type'>) {
    return this.show(message, { ...options, type: 'warning', duration: 4000 })
  }
}

// Export singleton instance
export const toast = new ToastManager()

// Export individual methods for convenience
export const showToast = toast.show.bind(toast)
export const showError = toast.error.bind(toast)
export const showInfo = toast.info.bind(toast)
export const showWarning = toast.warning.bind(toast)
